import { mapHexCodeToEmoticon } from '@assembly-web/services';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  Content,
  DropdownMenu,
  Portal,
  Root,
  Trigger,
} from '@radix-ui/react-dropdown-menu';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import type {
  SelectableListProps,
  SelectableOptionProps,
} from '../SelectableList';
import { SelectableList } from '../SelectableList';

export type SelectableListDropdownProps = {
  selectableListOptions: SelectableOptionProps[];
  label: string;
  postFixLabel: string;
  onDoneClick: ({
    label,
    currentSelectedOptions,
  }: {
    label: string;
    currentSelectedOptions: SelectableOptionProps[];
  }) => void;
  selectedOptions: SelectableOptionProps[];
  showPostFixLabelOnly?: boolean;
  onSearchChange?: ({ label, value }: { label: string; value: string }) => void;
  limit?: number;
} & Pick<
  SelectableListProps,
  | 'isLoading'
  | 'showSearch'
  | 'onLoadMore'
  | 'renderOption'
  | 'isFetchingNextPage'
  | 'hasNextPage'
  | 'loader'
  | 'onClearAllClick'
>;

export function SelectableListDropdown({
  isLoading,
  showSearch,
  onLoadMore,
  renderOption,
  onDoneClick,
  onSearchChange,
  isFetchingNextPage,
  selectableListOptions,
  label,
  postFixLabel,
  hasNextPage,
  selectedOptions,
  limit = 1,
  loader,
  onClearAllClick,
}: SelectableListDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const onOpenChange = () => {
    if (isOpen) {
      onSearchChange?.({ label, value: '' });
    }
    setIsOpen(!isOpen);
  };

  const generateFilterLabel = () => {
    if (selectedOptions.length === 0) {
      return label;
    } else if (selectedOptions.length <= limit) {
      return `${label}: ${selectedOptions
        .map(
          (option) =>
            `${
              option.emoticon && typeof option.emoticon === 'string'
                ? mapHexCodeToEmoticon(option.emoticon)
                : ''
            }${option.value}`
        )
        .join(', ')}`;
    } else {
      return `${label}: ${selectedOptions.length} ${postFixLabel}`;
    }
  };

  return (
    <Root>
      <DropdownMenu
        modal={false}
        open={isOpen}
        onOpenChange={() => {
          onOpenChange();
        }}
      >
        <Trigger className="focus-visible:rounded-lg">
          <div
            className={twMerge(
              'flex max-w-[280px] items-center gap-2 rounded-lg border border-gray-5 bg-gray-1 px-2 py-1 align-middle hover:bg-gray-3 focus:bg-gray-3',
              selectedOptions.length &&
                'bg-gray-3 hover:bg-gray-4 focus:bg-gray-4'
            )}
          >
            <div className="flex flex-wrap items-center truncate">
              <TextStyle
                as="p"
                variant={selectedOptions.length ? 'sm-medium' : 'sm-regular'}
                className="truncate text-gray-9"
              >
                {generateFilterLabel()}
              </TextStyle>
            </div>
            <AnimatePresence>
              <motion.div
                animate={isOpen ? 'opened' : 'closed'}
                variants={{
                  closed: { rotate: 0 },
                  opened: { rotate: -180 },
                }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDownIcon
                  color="gray-9"
                  className="h-4 w-4 text-gray-9"
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </Trigger>
        <Portal>
          <Content
            align="start"
            sideOffset={5}
            alignOffset={-3}
            className="rounded-lg border border-gray-5 bg-gray-1 shadow-md-down"
          >
            <SelectableList
              isLoading={isLoading}
              onLoadMore={onLoadMore}
              onDoneClick={(currentSelectedOptions) => {
                setIsOpen(false);
                onDoneClick({ label, currentSelectedOptions });
              }}
              onClearAllClick={() => {
                onClearAllClick();
                setIsOpen(false);
              }}
              options={selectableListOptions}
              selectedOptions={selectedOptions}
              onSearchChange={(val) => onSearchChange?.({ label, value: val })}
              renderOption={renderOption}
              className="max-w-screen"
              showSearch={showSearch}
              placeholder="Search"
              showNestedDropdownHeader={false}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              loader={loader}
            />
          </Content>
        </Portal>
      </DropdownMenu>
    </Root>
  );
}
